import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Calendar, 
  FileText, 
  Target, 
  TrendingUp, 
  School, 
  GraduationCap, 
  BookOpen, 
  Briefcase, 
  DollarSign, 
  Settings, 
  User,
  BarChart3,
  LogOut,
  ChevronDown,
  Bell,
  Shield,
  Search,
  Pin,
  PinOff,
  MessageSquare,
  Ticket
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { getRoleDisplayName } from '@varkwave/core';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  badge?: number;
  description?: string;
  category?: 'dashboard' | 'leadmanagement' | 'prmanagement' | 'attendance' | 'school' | 'sales' | 'hr' | 'admin';
}


const navigationItems: NavItem[] = [
  // Dashboard section - role-based dashboard access
  { 
    name: 'Today\'s Pulse', 
    href: '/dashboard/todays-pulse', 
    icon: BarChart3, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP'],
    description: 'Today\'s tasks, appointments, and follow-ups',
    category: 'dashboard'
  },
  { 
    name: 'Leads Dashboard', 
    href: '/dashboard/leads', 
    icon: Target, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP'],
    description: 'Lead conversion and sales metrics',
    category: 'dashboard'
  },
  { 
    name: 'Attendance Dashboard', 
    href: '/dashboard/attendance', 
    icon: Calendar, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'HR_MANAGER'],
    description: 'Employee attendance and time tracking',
    category: 'dashboard'
  },
  { 
    name: 'HR Dashboard', 
    href: '/dashboard/hr', 
    icon: Briefcase, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER'],
    description: 'Human resources and payroll',
    category: 'dashboard'
  },
  { 
    name: 'Analytics Dashboard', 
    href: '/dashboard/analytics', 
    icon: BarChart3, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER'],
    description: 'Comprehensive analytics and reporting',
    category: 'dashboard'
  },
  
  // Lead Management section - visible to Org Head and Sales team
  { 
    name: 'Leads', 
    href: '/leads', 
    icon: Target, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP'],
    description: 'Lead management and admissions pipeline',
    category: 'leadmanagement'
  },
  { 
    name: 'Config', 
    href: '/sales/config', 
    icon: Settings, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER'],
    description: 'Sales configuration and settings',
    category: 'leadmanagement'
  },
  { 
    name: '📱 WATI WhatsApp', 
    href: '/sales/chatbot-config', 
    icon: MessageSquare, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER'],
    description: 'Configure WATI WhatsApp integration and templates',
    category: 'leadmanagement'
  },
  { 
    name: 'Tasks', 
    href: '/sales/tasks', 
    icon: FileText, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP', 'SALES_REP'],
    description: 'Sales tasks and follow-ups',
    category: 'leadmanagement'
  },
  { 
    name: 'Call Logs', 
    href: '/sales/call-logs', 
    icon: TrendingUp, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP', 'SALES_REP'],
    description: 'Communication history and logs',
    category: 'leadmanagement'
  },
  { 
    name: 'Appointments', 
    href: '/appointments', 
    icon: Calendar, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP', 'SALES_REP'],
    description: 'Scheduled meetings and visits',
    category: 'leadmanagement'
  },
  { 
    name: 'Reports', 
    href: '/reports', 
    icon: BarChart3, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER'],
    description: 'Sales performance reports and analytics',
    category: 'leadmanagement'
  },
  
  // PR Management section - visible to PR Managers and above
  { 
    name: 'Tickets', 
    href: '/tickets', 
    icon: Ticket, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'PR_MANAGER', 'SALES_MANAGER'],
    description: 'Parent relationship management tickets',
    category: 'prmanagement'
  },
  { 
    name: 'Parent Communication', 
    href: '/parent-communication', 
    icon: MessageSquare, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'PR_MANAGER'],
    description: 'Communication with parents',
    category: 'prmanagement'
  },
  { 
    name: 'PR Analytics', 
    href: '/pr-analytics', 
    icon: BarChart3, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'PR_MANAGER'],
    description: 'Parent relationship analytics',
    category: 'prmanagement'
  },
  
  // Attendance section - Only for Sales Manager and above
  { 
    name: 'Attendance Management', 
    href: '/attendance', 
    icon: Calendar, 
    description: 'Track team attendance with geo-location',
    category: 'attendance',
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'HR_MANAGER']
  },
  { 
    name: 'Leave Management', 
    href: '/leaves', 
    icon: FileText, 
    description: 'Manage leave applications and view attendance',
    category: 'attendance'
  },
  
  // School Management
  { 
    name: 'Student Management', 
    href: '/school-management/students', 
    icon: GraduationCap, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER'],
    description: 'Student enrollment, records, and bulk operations',
    category: 'school'
  },
  { 
    name: 'Grade Configuration', 
    href: '/school-management/grades', 
    icon: BookOpen, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL'],
    description: 'Manage grades and fee structures',
    category: 'school'
  },
  { 
    name: 'Academic Progression', 
    href: '/school-management/academic-progression', 
    icon: TrendingUp, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL'],
    description: 'Year-end grade promotions and academic year management',
    category: 'school'
  },
  { 
    name: 'Notification Settings', 
    href: '/school-management/notifications', 
    icon: Bell, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL'],
    description: 'Parent notification controls and settings',
    category: 'school'
  },
  { 
    name: 'School Overview', 
    href: '/school-management', 
    icon: School, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'TEACHER', 'HR_MANAGER'],
    description: 'School dashboard and overview',
    category: 'school'
  },
  
  { 
    name: 'Sales Pipeline', 
    href: '/sales', 
    icon: TrendingUp, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP', 'SALES_REP'],
    description: 'Sales tracking and reports',
    category: 'sales'
  },
  
  // HR & Payroll
  { 
    name: 'Staff Management', 
    href: '/users', 
    icon: Users, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'HR_MANAGER'],
    description: 'Manage staff and permissions',
    category: 'hr'
  },
  { 
    name: 'HR Portal', 
    href: '/hr', 
    icon: Briefcase, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER'],
    description: 'Human resources management',
    category: 'hr'
  },
  { 
    name: 'Payroll', 
    href: '/payroll', 
    icon: DollarSign, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'HR_MANAGER', 'ACCOUNTANT'],
    description: 'Salary and payroll processing',
    category: 'hr'
  },
  
  // Analytics & Admin
  { 
    name: 'Analytics', 
    href: '/analytics', 
    icon: BarChart3, 
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL', 'SALES_MANAGER', 'HR_MANAGER'],
    description: 'Business intelligence and reports',
    category: 'admin'
  },
  { 
    name: 'Holiday Management', 
    href: '/admin/holidays', 
    icon: Calendar, 
    roles: ['ORG_HEAD', 'TENANT_HEAD'],
    description: 'Manage organizational holidays',
    category: 'admin'
  },
  { 
    name: 'User Control', 
    href: '/admin/users', 
    icon: Users, 
    roles: ['ORG_HEAD', 'ORG_ADMIN', 'TENANT_HEAD', 'SALES_MANAGER'],
    description: 'Manage users and permissions',
    category: 'admin'
  },
  {
    name: 'System Management',
    href: '/app/settings',
    icon: Shield,
    roles: ['SUPER_ADMIN'],
    description: 'Organization profile, schools, departments, and per-school settings',
    category: 'admin'
  },
  {
    name: 'Org & School Settings',
    href: '/app/settings',
    icon: Settings,
    roles: ['SUPER_ADMIN', 'ORG_HEAD', 'ORG_ADMIN', 'TENANT_HEAD'],
    description: 'Organization profile, schools, departments, and per-school module settings',
    category: 'admin'
  },
];

const categoryConfig = {
  dashboard: { name: 'Dashboard', color: 'text-blue-600 bg-blue-50' },
  leadmanagement: { name: 'Lead Management', color: 'text-emerald-600 bg-emerald-50' },
  prmanagement: { name: 'PR Management', color: 'text-pink-600 bg-pink-50' },
  attendance: { name: 'Attendance', color: 'text-indigo-600 bg-indigo-50' },
  school: { name: 'School Management', color: 'text-green-600 bg-green-50' },
  sales: { name: 'Sales & CRM', color: 'text-purple-600 bg-purple-50' },
  hr: { name: 'HR & Payroll', color: 'text-orange-600 bg-orange-50' },
  admin: { name: 'Administration', color: 'text-gray-600 bg-gray-50' },
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, logout, hasAnyRole } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['dashboard', 'leadmanagement', 'prmanagement', 'attendance']);

  // Determine if sidebar should be expanded (either not collapsed or hovering)
  const isExpanded = !isCollapsed || isHovering;

  // When sidebar expands, automatically expand dashboard categories
  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    
    // If expanding sidebar, ensure dashboard category is expanded
    if (!newCollapsed && !expandedCategories.includes('dashboard')) {
      setExpandedCategories(prev => [...prev, 'dashboard']);
    }
  };

  // Handle mouse enter/leave for hover expansion
  const handleMouseEnter = () => {
    if (isCollapsed) {
      setIsHovering(true);
      // Ensure dashboard category is expanded when hovering
      if (!expandedCategories.includes('dashboard')) {
        setExpandedCategories(prev => [...prev, 'dashboard']);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  // Filter navigation items based on user role (supports dual roles)
  const filteredNavigation = navigationItems.filter(item => {
    if (!item.roles) return true;
    return user && hasAnyRole(item.roles);
  });

  // Group items by category
  const groupedNavigation = filteredNavigation.reduce((acc, item) => {
    const category = item.category || 'core';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, NavItem[]>);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <motion.div 
      className={cn(
        "bg-white shadow-xl border-r border-gray-200 flex flex-col h-full transition-all duration-300 ease-in-out relative z-10",
        isExpanded ? "w-72" : "w-20"
      )}
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header */}
      <div className={cn("border-b border-gray-100", isExpanded ? "p-6" : "p-4")}>
        <div className={cn("flex items-center", isExpanded ? "justify-between" : "flex-col space-y-3")}>
          <div className={cn("flex items-center", isExpanded ? "space-x-3" : "flex-col space-y-2")}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
              <img src="/logo.svg" alt="VarkWave" className="w-full h-full object-contain" />
            </div>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h1 className="text-xl font-bold text-gray-900">VarkWave</h1>
                <p className="text-xs text-gray-500 font-medium">School Management System</p>
              </motion.div>
            )}
          </div>
          <button
            onClick={handleToggleCollapse}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={isCollapsed ? "Pin sidebar" : "Unpin sidebar"}
          >
            {isCollapsed ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      {isExpanded && (
        <div className="p-4 border-b border-gray-100">
          <div className="flex space-x-2">
            <button className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
              <Search className="w-4 h-4" />
              <span className="text-sm font-medium">Search</span>
            </button>
            <button className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors relative">
              <Bell className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-4 space-y-6">
          {Object.entries(groupedNavigation).map(([category, items]) => {
            const categoryInfo = categoryConfig[category as keyof typeof categoryConfig];
            const isCategoryExpanded = expandedCategories.includes(category);
            
            return (
              <div key={category} className="space-y-2">
                {isExpanded && (
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                  >
                    <span>{categoryInfo.name}</span>
                    <ChevronDown 
                      className={cn(
                        "w-4 h-4 transition-transform",
                        isCategoryExpanded ? "rotate-180" : ""
                      )} 
                    />
                  </button>
                )}
                
                <AnimatePresence>
                  {(!isExpanded || isCategoryExpanded) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className={cn("space-y-1", !isExpanded && "space-y-2 px-2")}
                    >
                      {items.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                          <motion.div
                            key={item.name}
                            whileHover={!isExpanded ? {} : { x: 2 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Link
                              to={item.href}
                              className={cn(
                                'group flex items-center text-sm font-medium transition-all duration-200 relative rounded-xl',
                                !isExpanded ? 'px-0 py-3 justify-center' : 'space-x-3 px-3 py-2.5',
                                isActive
                                  ? (!isExpanded 
                                      ? 'text-white' 
                                      : 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-md border-2 border-blue-300')
                                  : 'text-gray-600 hover:text-gray-900',
                                !isActive && isExpanded && 'hover:bg-gray-50',
                                !isActive && !isExpanded && 'hover:bg-gray-100'
                              )}
                              title={!isExpanded ? item.name : undefined}
                            >
                              <div className={cn(
                                "flex items-center justify-center transition-all duration-200",
                                !isExpanded 
                                  ? (isActive 
                                      ? 'w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-lg' 
                                      : 'w-10 h-10 hover:bg-gray-100 rounded-xl')
                                  : 'w-5 h-5',
                                isActive && isExpanded ? 'text-blue-600' : '',
                                !isActive && isExpanded ? 'text-gray-400 group-hover:text-gray-600' : '',
                                isActive && !isExpanded ? 'text-white' : '',
                                !isActive && !isExpanded ? 'text-gray-400 group-hover:text-gray-600' : ''
                              )}>
                                <item.icon className="w-5 h-5 transition-all duration-200" />
                              </div>
                              
                              {isExpanded && (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <span className="truncate">{item.name}</span>
                                      {item.badge && (
                                        <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                          {item.badge}
                                        </span>
                                      )}
                                    </div>
                                    {item.description && (
                                      <p className="text-xs text-gray-500 truncate mt-0.5">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                </>
                              )}
                            </Link>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className={cn("space-y-3", !isExpanded && "flex flex-col items-center")}>
          <div className={cn("flex items-center space-x-3", !isExpanded && "flex-col space-x-0 space-y-2")}>
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                <span className="text-white font-semibold text-sm">
                  {user?.fullName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
            </div>
            
            {isExpanded && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.fullName || user?.username || 'Demo User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {getRoleDisplayName(user?.role || 'USER')}
                </p>
              </div>
            )}
          </div>
          
          {isExpanded && (
            <div className="flex space-x-2">
              <Link
                to="/profile"
                className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <User className="w-4 h-4" />
                <span>Profile</span>
              </Link>
              
              <button
                onClick={handleLogout}
                className="flex items-center justify-center px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {!isExpanded && (
            <button
              onClick={handleLogout}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;